import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Lock,
  Key,
  Check,
  Lightbulb,
  RotateCcw,
  X as CloseIcon,
  Briefcase,
  Trash2,
  Unlock,
  Terminal,
} from 'lucide-react';

/**
 * TutorialCipherModal - Standalone cipher UI for tutorial
 *
 * Does NOT connect to game state or backend.
 * Renders different themed UIs based on theme prop:
 * - noir: Safe dial UI
 * - miami_vice: Briefcase UI
 * - 90s_nyc: Terminal-style UI with green monochrome display and Atbash cipher grid
 *
 * Props:
 * - open: boolean
 * - onClose: function
 * - ciphertext: string (e.g., "FVHFOF IJE UIF FWJEFODF")
 * - hints: array of hint strings (e.g., ["Shift by one", "Count one ahead"])
 * - expectedSolution: string (e.g., "EUGENE HID THE EVIDENCE")
 * - onSubmit: function(solution) - called when user submits
 * - theme: 'noir' | '90s_nyc' | 'miami_vice'
 */
export const TutorialCipherModal = ({
  open,
  onClose,
  ciphertext,
  hints = [],
  expectedSolution,
  onSubmit,
  theme = 'noir'
}) => {
  const [solution, setSolution] = useState('');
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const dialRef = useRef(null);
  const spinTimeoutRef = useRef(null);
  const errorTimeoutRef = useRef(null);

  // Reset state when modal opens to prevent stale values
  useEffect(() => {
    if (open) {
      setSolution('');
      setRotation(0);
      setShowSuccess(false);
      setShowError(false);
      setIsSpinning(false);
    }

    // Cleanup timeouts on unmount or when modal closes
    return () => {
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current);
      }
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
    };
  }, [open]);

  // Dial letters for visual effect (used by safe dial)
  const dialLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Theme configuration
  const themeConfig = {
    noir: {
      accent: '#DC143C',
      bgGradient: 'from-slate-950 via-slate-900 to-slate-950',
      title: 'Coded Message',
      subtitle: 'Crack the safe to decode the message',
      buttonText: 'Crack the Safe'
    },
    '90s_nyc': {
      accent: '#00FF00',
      bgGradient: 'from-zinc-950 via-zinc-900 to-zinc-950',
      title: 'Encrypted Note',
      subtitle: 'Decrypt the terminal message',
      buttonText: 'Decrypt'
    },
    miami_vice: {
      accent: '#FF1493',
      bgGradient: 'from-purple-950 via-slate-900 to-cyan-950',
      title: 'Coded Transmission',
      subtitle: 'Decode the intercepted message',
      buttonText: 'Open Case'
    }
  };

  const currentTheme = themeConfig[theme] || themeConfig.noir;

  // Handle input change (different per theme)
  const handleInputChange = (value) => {
    // For miami_vice (briefcase) and 90s_nyc (terminal): letters, spaces, and punctuation
    // For noir (safe dial): only letters and spaces
    const sanitized = (theme === 'miami_vice' || theme === '90s_nyc')
      ? value.toUpperCase().replace(/[^A-Z0-9 .,!?'-]/g, '')
      : value.toUpperCase().replace(/[^A-Z ]/g, '');
    setSolution(sanitized);

    // Spin dial on each character (only for noir theme with safe dial)
    if (theme === 'noir' && sanitized.length > solution.length) {
      const newChar = sanitized.charAt(sanitized.length - 1);
      const charIndex = dialLetters.indexOf(newChar);
      if (charIndex >= 0) {
        setIsSpinning(true);
        setRotation(-(charIndex * (360 / 26)));
        // Clear any existing spin timeout before setting new one
        if (spinTimeoutRef.current) {
          clearTimeout(spinTimeoutRef.current);
        }
        spinTimeoutRef.current = setTimeout(() => setIsSpinning(false), 300);
      }
    }
  };

  // Handle letter click on dial (visual only - updates input)
  const handleDialClick = (letter) => {
    if (!showSuccess) {
      handleInputChange(solution + letter);
    }
  };

  // Clear solution
  const handleClear = () => {
    setSolution('');
    setRotation(0);
    setShowError(false);
    // Clear any pending error timeout
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
  };

  // Handle submit
  const handleSubmitClick = () => {
    if (!solution.trim()) return;

    // In tutorial, auto-accept the correct solution
    // Compare normalized (trimmed, uppercase, single-spaced)
    const normalized = solution.trim().toUpperCase().replace(/\s+/g, ' ');
    const expectedNormalized = expectedSolution?.toUpperCase().replace(/\s+/g, ' ') || '';

    // Defensive guard: if expectedSolution is empty/missing (theme misroute,
    // bad config), warn loudly instead of silently treating "" === "" as a pass
    // OR (worse, in the false branch) silently rejecting every attempt and
    // failing to advance the tutorial.
    if (!expectedNormalized) {
      console.warn('[TutorialCipherModal] expectedSolution is empty — cipher cannot advance. Check TUTORIAL_CIPHER_DATA theme key.');
      setShowError(true);
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => setShowError(false), 3000);
      return;
    }

    if (normalized === expectedNormalized) {
      setShowSuccess(true);
      setShowError(false);
      // Notify parent that solution was entered correctly
      if (onSubmit) {
        onSubmit(solution.trim());
      }
    } else {
      // Show error but don't block - this is tutorial, they can retry
      setShowError(true);
      // Clear any existing error timeout before setting new one
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
      }
      errorTimeoutRef.current = setTimeout(() => setShowError(false), 3000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && solution.trim() && !showSuccess) {
      handleSubmitClick();
    }
  };

  // Handle dialog close - only allow closing after success (for tutorial flow)
  const handleDialogClose = (isOpen) => {
    // Only allow closing if the modal is being closed (isOpen = false)
    // AND we've shown success (user clicked "Continue Investigation" button)
    // This prevents ESC/backdrop clicks from closing during tutorial steps
    if (!isOpen && showSuccess) {
      onClose();
    }
  };

  // Render Safe Dial Visual (for noir theme)
  const renderSafeDialVisual = () => (
    <div className="flex flex-col items-center">
      <div className="relative w-64 h-64 mx-auto mb-4">
        {/* Outer ring - metallic safe border */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-zinc-600 via-zinc-800 to-zinc-900 shadow-2xl border-4 border-zinc-500">
          {/* Inner dial shadow */}
          <div className="absolute inset-4 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-900 shadow-inner" />
        </div>

        {/* Rotating dial */}
        <div
          ref={dialRef}
          className={`absolute inset-6 rounded-full bg-gradient-to-br from-zinc-800 to-black border-2 border-zinc-600 transition-transform ${isSpinning ? 'duration-300' : 'duration-100'}`}
          style={{ transform: `rotate(${rotation}deg)` }}
        >
          {/* Dial markings */}
          {dialLetters.map((letter, i) => {
            const angle = (i * 360) / 26;
            const radians = (angle - 90) * (Math.PI / 180);
            const radius = 85;
            const x = 50 + Math.cos(radians) * (radius / 2 + 15);
            const y = 50 + Math.sin(radians) * (radius / 2 + 15);

            return (
              <button
                key={letter}
                onClick={() => handleDialClick(letter)}
                disabled={showSuccess}
                aria-label={`Select letter ${letter} on cipher dial`}
                title={`Add ${letter} to solution`}
                className="absolute text-xs font-bold text-red-500 hover:text-red-300 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  left: `${x}%`,
                  top: `${y}%`,
                  transform: `translate(-50%, -50%) rotate(${-rotation}deg)`,
                }}
              >
                {letter}
              </button>
            );
          })}

          {/* Center dial knob */}
          <div className="absolute inset-16 rounded-full bg-gradient-to-br from-zinc-600 to-zinc-900 border-2 border-zinc-500 flex items-center justify-center shadow-lg">
            <Lock className="w-8 h-8 text-red-600" />
          </div>
        </div>

        {/* Dial pointer */}
        <div className="absolute top-2 left-1/2 transform -translate-x-1/2">
          <div className="w-0 h-0 border-l-4 border-r-4 border-b-8 border-l-transparent border-r-transparent border-b-red-600" />
        </div>
      </div>

      {/* Dial instructions */}
      <p className="text-xs text-slate-500 italic text-center">
        Click letters on the dial or type your solution below
      </p>
    </div>
  );

  // Render Briefcase Visual (for miami_vice theme)
  const renderBriefcaseVisual = () => (
    <div className="relative mx-auto max-w-md">
      {/* Briefcase body */}
      <div className="bg-gradient-to-b from-amber-950 via-amber-900 to-amber-950 rounded-lg border-4 border-amber-800 shadow-2xl p-4">
        {/* Handle */}
        <div className="flex justify-center mb-4">
          <div className="w-24 h-3 bg-gradient-to-r from-zinc-700 via-zinc-500 to-zinc-700 rounded-full border border-zinc-600" />
        </div>

        {/* Briefcase interior - document compartment */}
        <div className="bg-zinc-900 rounded-lg border-2 border-zinc-700 p-4 shadow-inner">
          {/* Lock status indicator */}
          <div
            className="flex justify-center gap-2 items-center mb-4"
            role="status"
            aria-live="polite"
            aria-label={showSuccess ? 'Case opened' : 'Case locked'}
          >
            {showSuccess ? (
              <Unlock className="w-5 h-5 text-green-500" aria-hidden="true" />
            ) : (
              <Lock className="w-5 h-5 text-orange-500" aria-hidden="true" />
            )}
            <div className={`w-3 h-3 rounded-full ${showSuccess ? 'bg-green-500' : 'bg-red-500'} shadow-lg animate-pulse`} aria-hidden="true" />
            <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">
              {showSuccess ? 'Case Opened' : 'Case Locked'}
            </span>
          </div>

          {/* Document slot visual */}
          <div className="bg-zinc-800 border border-zinc-600 rounded p-3 mb-3">
            <p className="text-xs text-zinc-500 font-mono uppercase mb-2 tracking-wider">
              Enter Decoded Message:
            </p>
            <div className="bg-black border-2 border-orange-800/50 rounded p-2 min-h-[60px] font-mono text-orange-400 text-lg tracking-wider break-all">
              {solution || <span className="text-zinc-600">_</span>}
            </div>
          </div>
        </div>

        {/* Latches */}
        <div className="flex justify-between mt-4 px-4">
          <div className="w-8 h-4 bg-gradient-to-b from-zinc-400 to-zinc-600 rounded-sm border border-zinc-500" />
          <div className="w-8 h-4 bg-gradient-to-b from-zinc-400 to-zinc-600 rounded-sm border border-zinc-500" />
        </div>
      </div>
    </div>
  );

  // Render Terminal Visual (for 90s_nyc theme)
  const renderTerminalVisual = () => {
    // Generate Atbash cipher grid (A↔Z, B↔Y, C↔X, etc.)
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const atbashMap = alphabet.split('').map((letter, i) => ({
      plain: letter,
      cipher: alphabet[25 - i]
    }));

    return (
      <div className="relative mx-auto max-w-lg">
        {/* Terminal frame */}
        <div className="bg-zinc-900 rounded border-2 border-green-900 shadow-2xl">
          {/* Terminal header */}
          <div className="bg-zinc-800 border-b border-green-900 px-3 py-2 flex items-center gap-2">
            <Terminal className="w-4 h-4 text-green-500" />
            <span className="text-xs text-green-500 font-mono uppercase tracking-wider">
              CIPHER_TERMINAL v2.1
            </span>
            <div className="ml-auto flex gap-1">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            </div>
          </div>

          {/* Terminal screen */}
          <div className="bg-black p-4 font-mono text-sm">
            {/* Status indicator */}
            <div className="flex items-center gap-2 mb-3">
              {showSuccess ? (
                <Unlock className="w-4 h-4 text-green-500" />
              ) : (
                <Lock className="w-4 h-4 text-green-500" />
              )}
              <span className="text-green-500 text-xs">
                {showSuccess ? '[DECRYPTED]' : '[ENCRYPTED]'}
              </span>
            </div>

            {/* Atbash cipher grid */}
            <div className="mb-3 p-3 border border-green-900/50 bg-green-950/20">
              <div className="text-xs text-green-600 mb-2">ATBASH CIPHER MAP:</div>
              <div className="grid grid-cols-6 sm:grid-cols-7 gap-x-2 gap-y-1 text-xs text-green-500">
                {atbashMap.map(({ plain, cipher }) => (
                  <div key={plain} className="text-center">
                    <div className="text-green-400">{plain}</div>
                    <div className="text-green-700">↕</div>
                    <div className="text-green-400">{cipher}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Solution display */}
            <div className="border border-green-900/50 bg-green-950/20 p-3">
              <div className="text-xs text-green-600 mb-1">DECODED OUTPUT:</div>
              <div className="min-h-[40px] text-green-500 text-base tracking-wider font-bold">
                {solution || <span className="text-green-900 animate-pulse">_</span>}
              </div>
            </div>
          </div>

          {/* Terminal footer */}
          <div className="bg-zinc-800 border-t border-green-900 px-3 py-1">
            <span className="text-xs text-green-700 font-mono">
              READY
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Render Ciphertext Display (theme-specific styling)
  const renderCiphertextDisplay = () => {
    if (theme === 'miami_vice') {
      // Briefcase style - evidence tag
      return (
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg overflow-hidden">
          <div className="bg-orange-600/20 border-b border-orange-600/50 px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-orange-500" />
              <span className="text-xs text-orange-400 uppercase font-mono tracking-wider">Evidence Tag</span>
            </div>
          </div>
          <div className="p-4">
            <p className="font-mono text-lg text-zinc-200 tracking-widest leading-relaxed break-all">
              {ciphertext}
            </p>
          </div>
        </div>
      );
    }

    if (theme === '90s_nyc') {
      // Terminal style - encrypted message display
      return (
        <div className="bg-black border border-green-900 rounded overflow-hidden">
          <div className="bg-green-950/50 border-b border-green-900 px-4 py-2 flex items-center gap-2">
            <Terminal className="w-4 h-4 text-green-500" />
            <span className="text-xs text-green-500 uppercase font-mono tracking-wider">Encrypted Message</span>
          </div>
          <div className="p-4 bg-black">
            <p className="font-mono text-lg text-green-500 tracking-widest leading-relaxed break-all">
              {ciphertext}
            </p>
          </div>
        </div>
      );
    }

    // Safe dial style (noir) - vintage note
    return (
      <div className="bg-amber-900/20 border-2 border-amber-800/50 rounded-lg p-4 relative">
        <div className="absolute -top-3 left-4 bg-slate-900 px-2">
          <span className="text-xs text-amber-600 uppercase tracking-wider font-serif">Encoded Message</span>
        </div>
        <p className="font-serif text-lg text-amber-200 tracking-widest leading-relaxed break-all pt-2">
          {ciphertext}
        </p>
      </div>
    );
  };

  // Render Hints Section (theme-specific styling)
  const renderHints = () => {
    if (!hints || hints.length === 0) return null;

    if (theme === 'miami_vice') {
      // Briefcase style - intel gathered
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-yellow-500" />
            <span className="text-sm font-mono text-yellow-400">INTEL GATHERED</span>
          </div>
          <div className="bg-zinc-900 rounded border border-zinc-700 divide-y divide-zinc-800">
            {hints.map((hint, idx) => (
              <div key={`${idx}-${hint}`} className="p-3 flex items-start gap-2">
                <span className="text-orange-500 font-mono text-xs">#{idx + 1}</span>
                <p className="text-sm text-zinc-300 font-mono">"{hint}"</p>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (theme === '90s_nyc') {
      // Terminal style - system hints
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-green-500" />
            <span className="text-sm font-mono text-green-400">SYSTEM HINTS</span>
          </div>
          <div className="bg-black rounded border border-green-900 divide-y divide-green-900/50">
            {hints.map((hint, idx) => (
              <div key={`${idx}-${hint}`} className="p-3">
                <div className="flex items-start gap-2">
                  <span className="text-green-600 font-mono text-xs">[{idx + 1}]</span>
                  <p className="text-sm text-green-500 font-mono">"{hint}"</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Safe dial style (noir) - case notes
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-serif text-amber-400">Case Notes</span>
        </div>
        <div className="bg-slate-800/50 rounded border border-slate-700 p-3 space-y-2">
          {hints.map((hint, idx) => (
            <p key={`${idx}-${hint}`} className="text-sm text-slate-300 font-serif italic border-l-2 border-red-600 pl-3">
              "{hint}"
            </p>
          ))}
        </div>
      </div>
    );
  };

  // Render Input Field (theme-specific styling)
  const renderInputField = () => {
    if (theme === 'miami_vice') {
      // Briefcase style - modern input
      return (
        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={solution}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="ENTER DECODED MESSAGE..."
              disabled={showSuccess}
              className="flex-1 bg-zinc-900 border-2 border-orange-800/50 rounded px-4 py-3 text-white font-mono tracking-widest placeholder:text-zinc-600 focus:border-orange-500 focus:outline-none disabled:opacity-50"
              data-tutorial="cipher-input"
            />
            <button
              onClick={handleClear}
              disabled={showSuccess}
              className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-400 hover:text-zinc-200 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    }

    if (theme === '90s_nyc') {
      // Terminal style - green monochrome input
      return (
        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              value={solution}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="ENTER DECODED MESSAGE..."
              disabled={showSuccess}
              className="flex-1 bg-black border-2 border-green-900 rounded px-4 py-3 text-green-500 font-mono tracking-widest placeholder:text-green-900 focus:border-green-500 focus:outline-none disabled:opacity-50"
              data-tutorial="cipher-input"
            />
            <button
              onClick={handleClear}
              disabled={showSuccess}
              className="px-3 py-2 bg-black border border-green-900 rounded text-green-600 hover:text-green-400 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      );
    }

    // Safe dial style (noir) - typewriter input
    return (
      <div className="space-y-3">
        <div className="relative">
          <input
            type="text"
            value={solution}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="ENTER DECODED MESSAGE..."
            disabled={showSuccess}
            className="w-full bg-slate-900 border-2 border-red-900/50 rounded-lg px-4 py-3 text-white font-serif tracking-widest placeholder:text-slate-600 focus:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-600/30 disabled:opacity-50"
            data-tutorial="cipher-input"
          />
          {solution && !showSuccess && (
            <button
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent
        className={`w-full h-[100dvh] sm:max-w-2xl sm:h-auto sm:max-h-[90vh] overflow-y-auto bg-gradient-to-b ${currentTheme.bgGradient} border-2`}
        style={{ borderColor: currentTheme.accent }}
        data-tutorial="cipher-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2 pr-10">
            <Lock className="w-5 h-5" style={{ color: currentTheme.accent }} />
            {currentTheme.title}
          </DialogTitle>
          <p className="text-sm text-slate-400">{currentTheme.subtitle}</p>
        </DialogHeader>

        <div className="space-y-6">
          {/* Theme-specific visual: miami_vice uses briefcase, 90s_nyc uses terminal, noir uses safe dial */}
          {theme === 'miami_vice' ? renderBriefcaseVisual() : theme === '90s_nyc' ? renderTerminalVisual() : renderSafeDialVisual()}

          {/* Ciphertext Display */}
          {renderCiphertextDisplay()}

          {/* Discovered Hints */}
          {renderHints()}

          {/* Success Display */}
          {showSuccess && (
            <div
              className="p-4 rounded-lg border-2 bg-green-900/30 border-green-600 animate-success-glow"
              style={{ boxShadow: '0 0 30px rgba(34, 197, 94, 0.4)' }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Check className="w-6 h-6 text-green-500 animate-bounce" />
                <span className={`font-bold text-lg text-green-400 ${theme === 'noir' ? 'font-serif' : 'font-mono'}`}>
                  {theme === 'miami_vice' ? 'CASE CRACKED' : theme === '90s_nyc' ? 'DECRYPTION SUCCESS' : 'Safe Cracked!'}
                </span>
              </div>
              <p className={`text-sm text-green-300 ${theme === 'noir' ? 'font-serif' : 'font-mono'}`}>
                Perfect! You've successfully decoded the cipher.
              </p>
              {expectedSolution && (
                <div className="mt-3 p-3 bg-green-800/30 rounded border border-green-600">
                  <p className="text-xs text-green-400 uppercase tracking-wider mb-1">Decoded Message:</p>
                  <p className={`text-green-200 font-bold text-lg ${theme === 'noir' ? 'font-serif' : 'font-mono'}`}>
                    {expectedSolution}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Error Display */}
          {showError && (
            <div className="p-4 rounded-lg border-2 bg-red-900/30 border-red-600 animate-shake">
              <div className="flex items-center gap-2">
                <CloseIcon className="w-5 h-5 text-red-500" />
                <span className={`font-bold text-red-400 ${theme === 'noir' ? 'font-serif' : 'font-mono'}`}>
                  {theme === 'miami_vice' ? 'WRONG CODE' : theme === '90s_nyc' ? 'DECRYPTION FAILED' : 'Not quite right'}
                </span>
              </div>
              <p className={`text-sm text-red-300 mt-2 ${theme === 'noir' ? 'font-serif' : 'font-mono'}`}>
                {theme === '90s_nyc'
                  ? 'Check the Atbash cipher map and try again.'
                  : 'Check the hints and try again. Remember to use the Caesar cipher shift.'}
              </p>
            </div>
          )}

          {/* Solution Input */}
          {renderInputField()}

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={handleSubmitClick}
              disabled={!solution.trim() || showSuccess}
              className={`flex-1 text-white border-2 ${
                theme === 'miami_vice'
                  ? 'bg-orange-600 hover:bg-orange-500 font-mono border-orange-700'
                  : theme === '90s_nyc'
                  ? 'bg-green-700 hover:bg-green-600 font-mono border-green-900'
                  : 'bg-red-700 hover:bg-red-600 font-serif border-red-900'
              }`}
              data-tutorial="cipher-submit-button"
            >
              <Key className="w-4 h-4 mr-2" />
              {currentTheme.buttonText}
            </Button>
          </div>

          {/* Close button (appears after success) */}
          {showSuccess && (
            <Button
              onClick={onClose}
              variant="outline"
              className="w-full border-slate-600 text-white hover:bg-slate-800"
              data-tutorial="cipher-close-button"
            >
              Continue Investigation
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TutorialCipherModal;
